import React, { useState } from 'react';

export default function AdminPaymentsSection({ appId }) {
  const [expandedSections, setExpandedSections] = useState({
    stripe: false,
    stripeSetup: false,
    stripeTransfer: false,
    testCards: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>💳</span>
          <span>Payments</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Payment processing and billing configuration
        </p>
      </div>

      <div className="space-y-4">
        {/* Stripe Configuration */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('stripe')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">Stripe</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.stripe ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.stripe && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              <div className="space-y-4 text-sm text-gray-700">
                <p className="text-xs text-gray-600">
                  Stripe is a payment processing platform that enables businesses to accept payments online and in person. Manage your payment methods, subscriptions, and billing through the Stripe dashboard.
                </p>
                <div>
                  <a
                    href="https://dashboard.stripe.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-zapt-purple rounded-lg hover:bg-zapt-purple/90 transition-colors"
                  >
                    Open Stripe Dashboard →
                  </a>
                </div>

                {/* Stripe Account Setup Process - Nested */}
                <div className="border border-gray-300 rounded-lg bg-white">
                  <button
                    onClick={() => toggleSection('stripeSetup')}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <h5 className="text-xs font-semibold text-gray-900">Account Setup Process</h5>
                    <span className="text-gray-500 text-xs">
                      {expandedSections.stripeSetup ? '▼' : '▶'}
                    </span>
                  </button>
                  {expandedSections.stripeSetup && (
                    <div className="p-3 pt-3 border-t border-gray-200 bg-gray-50">
                      <div className="space-y-3">
                        <div className="p-2 bg-white border border-gray-200 rounded-lg">
                          <p className="font-semibold text-gray-900 text-xs mb-1">1. Create Stripe Account</p>
                          <p className="text-xs text-gray-600">
                            Create accounts using <code className="bg-gray-100 px-1 rounded">david+stripeapps@zapt.ai</code>
                          </p>
                        </div>

                        <div className="p-2 bg-white border border-gray-200 rounded-lg">
                          <p className="font-semibold text-gray-900 text-xs mb-1">2. Build in Test Mode</p>
                          <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                            <li>Configure API keys</li>
                            <li>Set up webhooks</li>
                            <li>Create products</li>
                            <li>Test everything in test mode first</li>
                          </ul>
                        </div>

                        <div className="p-2 bg-white border border-gray-200 rounded-lg">
                          <p className="font-semibold text-gray-900 text-xs mb-1">3. When Ready to Go Live</p>
                          <p className="text-xs text-gray-500 mb-1 italic">Note: You need to recreate everything for live mode - test and live are separate environments</p>
                          <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                            <li>Add client as Super Administrator</li>
                            <li>Transfer ownership to their email</li>
                            <li>Client completes KYC within 14 days</li>
                            <li>Switch to live mode in Stripe Dashboard</li>
                            <li>Configure live API keys in app environment</li>
                            <li>Set up webhooks again for live mode</li>
                            <li>Recreate all products/prices for live mode</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Transfer Ownership Steps - Nested */}
                <div className="border border-gray-300 rounded-lg bg-white">
                  <button
                    onClick={() => toggleSection('stripeTransfer')}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <h5 className="text-xs font-semibold text-gray-900">Transfer Ownership Steps</h5>
                    <span className="text-gray-500 text-xs">
                      {expandedSections.stripeTransfer ? '▼' : '▶'}
                    </span>
                  </button>
                  {expandedSections.stripeTransfer && (
                    <div className="p-3 pt-3 border-t border-gray-200 bg-gray-50">
                      <div className="space-y-3">
                        <div className="p-2 bg-white border border-gray-200 rounded-lg">
                          <p className="font-semibold text-gray-900 text-xs mb-1">1. Invite New Owner</p>
                          <p className="text-xs text-gray-600">
                            Go to <strong>Settings → Team</strong> in the Stripe Dashboard and invite the new owner as a <strong>Super Administrator</strong>
                          </p>
                        </div>

                        <div className="p-2 bg-white border border-gray-200 rounded-lg">
                          <p className="font-semibold text-gray-900 text-xs mb-1">2. Transfer Ownership</p>
                          <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                            <li>Once they accept, go back to Team settings</li>
                            <li>Click the overflow menu (⋯) next to their name</li>
                            <li>Select <strong>Transfer ownership</strong></li>
                          </ul>
                        </div>

                        <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="font-semibold text-amber-800 text-xs mb-0.5">Important</p>
                          <p className="text-xs text-amber-700">
                            Remain as a team admin for ongoing access to help with maintenance and support.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Test Cards - Nested */}
                <div className="border border-gray-300 rounded-lg bg-white">
                  <button
                    onClick={() => toggleSection('testCards')}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <h5 className="text-xs font-semibold text-gray-900">Test Cards</h5>
                    <span className="text-gray-500 text-xs">
                      {expandedSections.testCards ? '▼' : '▶'}
                    </span>
                  </button>
                  {expandedSections.testCards && (
                    <div className="p-3 pt-3 border-t border-gray-200 bg-gray-50">
                      <div className="space-y-3">
                        <p className="text-xs text-gray-600">
                          When testing interactively, use a card number, such as <code className="bg-gray-100 px-1 rounded font-mono">4242 4242 4242 4242</code>. Enter the card number in the Dashboard or in any payment form.
                        </p>

                        <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                          <li>Use a valid future date, such as <code className="bg-gray-100 px-1 rounded font-mono">12/34</code></li>
                          <li>Use any three-digit CVC (four digits for American Express cards)</li>
                        </ul>

                        <div>
                          <a
                            href="https://docs.stripe.com/testing"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-zapt-purple border border-zapt-purple rounded-lg hover:bg-zapt-purple/10 transition-colors"
                          >
                            View All Test Cards →
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
